/*
 * @Author: Claude
 * @Date: 2025-12-31
 * @Description: 统一的 Upload 组件（通过配置控制预览方式）
 */
import { FC, ReactNode } from 'react';

import { AIFunction } from '@/utils/const';

import { ExtendedUploadFile } from '../common/types';
import { FileUploadRule } from '../common/types/validator';
import { UploadConfig } from '../config/types';
import { GridUpload } from './GridUpload';
import { ListUpload } from './ListUpload';

/**
 * 预览类型枚举
 */
export type PreviewType =
  | 'image-grid'
  | 'video-grid'
  | 'audio-list'
  | 'media-list'
  | 'custom';

/**
 * 布局类型
 */
type LayoutType = 'grid' | 'list';

/**
 * 预览渲染器类型
 */
export interface PreviewRenderer {
  /** Grid 布局的预览渲染器 */
  grid?: (
    file: ExtendedUploadFile,
    onDelete: (e: React.MouseEvent) => void,
    onSelect?: () => void,
  ) => ReactNode;
  /** List 布局的预览渲染器 */
  list?: (
    originNode: React.ReactElement,
    file: ExtendedUploadFile,
    fileList: ExtendedUploadFile[],
    onDelete: (e: React.MouseEvent) => void,
  ) => ReactNode;
}

export interface UploadProps {
  /** 预览类型 */
  previewType?: PreviewType;
  /** 自定义布局类型（previewType 为 custom 时使用） */
  layout?: LayoutType;
  /** 自定义预览渲染器（previewType 为 custom 时使用） */
  renderPreview?: PreviewRenderer;
  /** 上传占位符 */
  uploadPlaceholder?: ReactNode;
  /** 文件列表（受控模式） */
  fileList?: ExtendedUploadFile[];
  /** Form.Item 传递的 value */
  value?: ExtendedUploadFile[];
  /** 文件列表变化回调 */
  onChange?: (fileList: ExtendedUploadFile[], file?: ExtendedUploadFile) => void;
  /** 最大文件数量 */
  maxCount?: number;
  /** 校验规则 */
  rules?: FileUploadRule[];
  /** 校验失败回调 */
  onValidationFailed?: (error: string) => void;
  /** 是否开启选择模式 */
  selectable?: boolean;
  /** 选中文件变化回调 */
  onSelectChange?: (file: ExtendedUploadFile | null) => void;
  /** 提示信息 */
  tips?: string | ReactNode;
  /** 底部提示文本 */
  footerTip?: string;
  /** 子元素（自定义上传区域） */
  children?: ReactNode;
  /** 文件类型限制 */
  accept?: string;
  /** 上传配置 */
  config: UploadConfig;
  /** 上传功能来源 */
  feature?: AIFunction;
  /** 是否启用埋点 */
  enableTracking?: boolean;
  /** 拖拽事件 */
  onDrop?: (e: React.DragEvent<HTMLDivElement>) => void;
}

/**
 * 统一的 Upload 组件
 * 根据 previewType 自动选择布局和预览方式
 */
export const Upload: FC<UploadProps> = (props) => {
  const { previewType = 'media-list', layout, renderPreview, ...restProps } = props;

  // 根据 previewType 确定布局类型
  const actualLayout: LayoutType =
    previewType === 'custom'
      ? layout || 'list'
      : previewType.includes('grid')
        ? 'grid'
        : 'list';

  // Grid 布局
  if (actualLayout === 'grid') {
    return (
      <GridUpload
        {...restProps}
        renderPreviewItem={
          renderPreview?.grid ||
          (() => {
            throw new Error('renderPreview.grid is required when layout is "grid"');
          })
        }
      />
    );
  }

  // List 布局
  return (
    <ListUpload
      {...restProps}
      renderPreviewItem={
        renderPreview?.list ||
        (() => {
          throw new Error('renderPreview.list is required when layout is "list"');
        })
      }
    />
  );
};
